import { useQuery, useQueryClient } from "react-query";
import { useCallback, useEffect, useRef } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/services/functions";
import { useStore } from "@/stores/useStore";
import { firestore } from "@/services/firestore";
import { OrderCountsMetric, MonthBucket } from "@/types/v2/PrecomputedMetrics";

export interface DateRangeRequest {
  dateRangeStart: number;
  dateRangeEnd: number;
}

export interface OrderCountsResult {
  current: {
    created: number;
    completed: number;
    open: number;
  };
  previous: {
    created: number;
    completed: number;
    open: number;
  };
  rollover: {
    count: number;
    rate: number; // percentage 0-100
  };
  months: MonthBucket[]; // last 12 months stacked open/closed
}

interface ComputeOrderCountsRequest {
  shopId: string;
  dateRangeStart: number;
  dateRangeEnd: number;
}

export interface ComputeOrderCountsResponse {
  cacheKey: string;
  metric: OrderCountsMetric | null;
  cached: boolean;
  isStale: boolean;
  backgroundRefresh: boolean;
  computationTimeMs: number;
  result: OrderCountsResult;
}

/**
 * Enhanced hook for fetching order counts with intelligent caching
 * Integrates with cloud functions and provides real-time cache invalidation
 * Follows the same pattern as useContainerTimeMetrics for consistency
 * @param params Date range parameters
 * @param params.start Start date for the order counts query
 * @param params.end End date for the order counts query
 */
export function useOrderCounts(params: { start: Date; end: Date }) {
  const { shop } = useStore();
  const queryClient = useQueryClient();
  const shopId = shop?.id;
  const lastCacheKeyRef = useRef<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Convert dates to timestamps for the API
  const requestConfig = {
    shopId: shopId!,
    dateRangeStart: params.start.getTime(),
    dateRangeEnd: params.end.getTime(),
  };

  // Generate stable query key
  const queryKey = ["orderCounts", shopId, requestConfig];

  // Create the callable function outside of query to avoid recreating
  const computeOrderCountsCallable = useCallback(() => {
    return httpsCallable<ComputeOrderCountsRequest, ComputeOrderCountsResponse>(
      functions,
      "functionsV2-computeOrderCounts"
    );
  }, []);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!shopId) throw new Error("Shop ID not available");

      const callable = computeOrderCountsCallable();
      const result = await callable(requestConfig);

      // Return the enhanced response from the cloud function which now includes cache metadata
      return result.data;
    },
    enabled: !!shopId,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60, // 1 hour - order counts change less frequently
    cacheTime: 1000 * 60 * 60 * 24, // 24 hours - keep in memory for 24 hours
    onError: (error) => {
      console.error("Error fetching order counts:", error);
    },
  });

  // Set up real-time cache invalidation listener for order counts metrics
  useEffect(() => {
    if (!shopId || !query.data?.cacheKey) {
      return;
    }

    const currentCacheKey = query.data.cacheKey;

    // Only set up a new subscription if cacheKey actually changed
    if (currentCacheKey === lastCacheKeyRef.current) {
      return;
    }

    // Clean up previous subscription
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    const metricPath = `shops/${shopId}/orderCountsMetrics/${currentCacheKey}`;
    let lastStaleState: boolean | undefined = undefined;

    unsubscribeRef.current = firestore.subscribeDocument<OrderCountsMetric>(
      metricPath,
      (snapshot) => {
        if (snapshot) {
          const metricData = snapshot as OrderCountsMetric;
          const currentStaleState = metricData.cache.isStale;

          // Only invalidate if cache became newly stale (transition from false/undefined to true)
          // This prevents loops from age-based staleness that doesn't require invalidation
          if (currentStaleState && lastStaleState === false) {
            // Use setTimeout to avoid immediate refetch during render
            setTimeout(() => {
              queryClient.invalidateQueries(queryKey);
            }, 100);
          }

          lastStaleState = currentStaleState;
        }
      }
    );

    lastCacheKeyRef.current = currentCacheKey;

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [shopId, query.data?.cacheKey, queryClient, queryKey]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  return {
    ...query,
    orderCounts: query.data?.result,
    cached: query.data?.cached || false,
    isStale: query.data?.isStale || false,
    backgroundRefresh: query.data?.backgroundRefresh || false,
    computationTimeMs: query.data?.computationTimeMs || 0,
    cacheKey: query.data?.cacheKey,
  };
}

/**
 * Query factory for consistent query key generation
 * @param shopId
 * @param dateRange
 * @param dateRange.start
 * @param dateRange.end
 */
export const orderCountsQuery = (
  shopId: string,
  dateRange: { start: Date; end: Date }
) => ({
  queryKey: [
    "orderCounts",
    shopId,
    {
      shopId,
      dateRangeStart: dateRange.start.getTime(),
      dateRangeEnd: dateRange.end.getTime(),
    },
  ],
  queryFn: async () => {
    const computeOrderCountsCallable = httpsCallable<
      ComputeOrderCountsRequest,
      ComputeOrderCountsResponse
    >(functions, "functionsV2-computeOrderCounts");

    const result = await computeOrderCountsCallable({
      shopId,
      dateRangeStart: dateRange.start.getTime(),
      dateRangeEnd: dateRange.end.getTime(),
    });

    return result.data;
  },
  refetchOnWindowFocus: false,
  staleTime: 1000 * 60 * 60, // 1 hour
  cacheTime: 1000 * 60 * 60 * 24, // 24 hours
});
